import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLossReasons } from '@/hooks/useLossReasons';
import { Plus, Loader2 } from 'lucide-react';

interface LossReasonDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (lossReasonId: string) => void;
  opportunityTitle?: string;
}

export function LossReasonDialog({ open, onClose, onConfirm, opportunityTitle }: LossReasonDialogProps) {
  const { lossReasons, loading, createLossReason } = useLossReasons();
  const [selectedReasonId, setSelectedReasonId] = useState<string>('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newReasonName, setNewReasonName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleSelectChange = (value: string) => {
    if (value === '__new__') {
      setIsAddingNew(true);
      setSelectedReasonId('');
    } else {
      setSelectedReasonId(value);
      setIsAddingNew(false);
    }
  };

  const handleCreateNewReason = async () => {
    if (!newReasonName.trim()) return;

    setIsCreating(true);
    const newReason = await createLossReason(newReasonName.trim());
    setIsCreating(false);

    if (newReason) {
      setSelectedReasonId(newReason.id);
      setNewReasonName('');
      setIsAddingNew(false);
    }
  };

  const handleConfirm = () => {
    if (selectedReasonId) {
      onConfirm(selectedReasonId);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedReasonId('');
    setIsAddingNew(false);
    setNewReasonName('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#18181B] border-[#3F3F464D] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Motivo da Perda</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {opportunityTitle && (
            <p className="text-sm text-neutral-400">
              Informe o motivo da perda para: <span className="text-white font-medium">{opportunityTitle}</span>
            </p>
          )}

          <div className="space-y-2">
            <Label className="text-white">Selecione o motivo</Label>
            <Select value={selectedReasonId} onValueChange={handleSelectChange} disabled={loading}>
              <SelectTrigger className="w-full bg-neutral-600 text-white border-[#3F3F46]">
                <SelectValue placeholder={loading ? "Carregando..." : "Selecione um motivo"} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {lossReasons.map((reason) => (
                    <SelectItem key={reason.id} value={reason.id}>
                      {reason.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="__new__" className="text-blue-400">
                    <span className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Adicionar novo motivo
                    </span>
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {isAddingNew && (
            <div className="space-y-2 p-3 bg-neutral-700/50 rounded-lg border border-neutral-600">
              <Label className="text-white">Novo motivo de perda</Label>
              <div className="flex gap-2">
                <Input
                  value={newReasonName}
                  onChange={(e) => setNewReasonName(e.target.value)}
                  placeholder="Ex: Preço alto, Concorrente..."
                  className="flex-1 bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateNewReason();
                    }
                  }}
                />
                <Button
                  onClick={handleCreateNewReason}
                  disabled={!newReasonName.trim() || isCreating}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-white text-white hover:bg-white hover:text-black"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedReasonId}
            className="bg-red-600 hover:bg-red-700"
          >
            Confirmar Perda
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
